import {
  Action,
  Alert,
  ConfirmModal,
  Loading,
  Modal,
  SimpleSelect,
  SpinnerIcon,
} from 'corso-admin-ui';
import {
  CrewClaimResolutionMethodEnum,
  crewClaimResolutionMethodEnumName,
  CrewClaimStatusCode,
  crewClaimStatuses,
} from 'corso-types';
import { useCallback, useState } from 'react';
import {
  canChangeResolution,
  isDefaultApproval,
  isRequestedAs,
  isRequestedOther,
  isResolvableResolutionMethod,
  resolutionMethodChangesAllowed,
  SupportedClaimLineItem,
  SupportedRequestedResolutionMethod,
  SupportedResolvedResolutionMethod,
  useIsLocked,
} from '~/features/claimReview/ClaimReviewProvider';
import useClaimLineItemUpdate, {
  ClaimLineItemUpdateCancellation,
  ClaimLineItemUpdateDenial,
} from '~/features/claimReview/hooks/useClaimLineItemUpdate';
import { resolutionModifier } from './resolutionModifiers/ResolutionModifier';

const deniedStatuses =
  crewClaimStatuses[CrewClaimStatusCode.denied].claimStatusDetail;

type DeniedStatus = (typeof deniedStatuses)[number]['code'];

function DenialModifier({
  onCancel,
  onSave,
}: {
  onCancel: () => void;
  onSave: (denial: ClaimLineItemUpdateDenial) => void;
}) {
  const [denialStatus, setDenialStatus] = useState<DeniedStatus>(
    deniedStatuses[2].code,
  );

  return (
    <div className="flex flex-col gap-4">
      <SimpleSelect
        label="Reason"
        value={denialStatus}
        options={deniedStatuses.map((deniedStatus) => ({
          label: deniedStatus.name,
          value: deniedStatus.code,
        }))}
        onChange={(selectedDenialStatus) =>
          setDenialStatus(selectedDenialStatus)
        }
      />
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
        <Action variant="ghost" onClick={onCancel}>
          Cancel
        </Action>
        <Action
          variant="destructive"
          onClick={() => {
            onSave({
              claimStatusDetailCode: denialStatus,
            });
          }}
        >
          Save
        </Action>
      </div>
    </div>
  );
}

const cancelStatuses =
  crewClaimStatuses[CrewClaimStatusCode.closed].claimStatusDetail;

type CancellationStatus = (typeof cancelStatuses)[number]['code'];

function CancellationModifier({
  onCancel,
  onSave,
}: {
  onCancel: () => void;
  onSave: (cancellation: ClaimLineItemUpdateCancellation) => void;
}) {
  const [cancelStatus, setCancelStatus] = useState<CancellationStatus>(
    cancelStatuses[0].code,
  );

  return (
    <div className="flex flex-col gap-4">
      <Alert heading="Cancel Claim" variant="warning">
        This will cancel the line item and remove it from the claim. All other
        line items will remain on the claim. This action cannot be undone.
      </Alert>
      <SimpleSelect
        label="Reason"
        value={cancelStatus}
        options={cancelStatuses.map((status) => ({
          label: status.name,
          value: status.code,
        }))}
        onChange={(selectedCancelStatus) =>
          setCancelStatus(selectedCancelStatus)
        }
      />
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
        <Action variant="ghost" onClick={onCancel}>
          Cancel
        </Action>
        <Action
          variant="caution"
          onClick={() => {
            onSave({
              claimStatusDetailCode: cancelStatus,
            });
          }}
        >
          Save
        </Action>
      </div>
    </div>
  );
}

function ClaimLineItemEdit({
  claimLineItem,
}: {
  claimLineItem: SupportedClaimLineItem<
    Exclude<
      SupportedRequestedResolutionMethod,
      typeof CrewClaimResolutionMethodEnum.other
    >
  >;
}) {
  const [showEdit, setShowEdit] = useState(false);

  const cancelEdit = useCallback(() => {
    setShowEdit(false);
  }, []);

  const supportedResolutions: readonly [
    SupportedResolvedResolutionMethod,
    ...SupportedResolvedResolutionMethod[],
  ] =
    resolutionMethodChangesAllowed[claimLineItem.requestedResolutionMethodEnum];

  const [selectedResolution, setSelectedResolution] = useState<
    (typeof supportedResolutions)[number] | 'Deny' | 'Cancel'
  >(() => {
    const [fallbackResolution] = supportedResolutions;

    if (
      claimLineItem.resolutionMethodEnum &&
      isResolvableResolutionMethod(claimLineItem.resolutionMethodEnum) &&
      supportedResolutions.includes(claimLineItem.resolutionMethodEnum)
    ) {
      return claimLineItem.resolutionMethodEnum;
    }

    return fallbackResolution;
  });

  const { mutate: onChange, isPending } = useClaimLineItemUpdate(
    claimLineItem.id,
  );

  const isUnevenExchange =
    !!claimLineItem.exchangeDeltaAmount &&
    claimLineItem.exchangeDeltaAmount !== 0;

  return (
    <>
      {isPending ? (
        <SpinnerIcon />
      ) : (
        <Action
          icon="edit"
          accessibilityLabel="Edit"
          variant="ghost"
          onClick={() => setShowEdit(true)}
          disabled={isPending}
        />
      )}
      <Modal
        title="Edit Resolution"
        show={showEdit}
        onClose={cancelEdit}
        size="lg"
      >
        <div className="flex flex-col gap-4">
          {isUnevenExchange && (
            <Alert
              variant="warning"
              heading={claimLineItem.isShopNow ? 'Shop Now' : 'Uneven Exchange'}
            >
              Editing this line item will forfeit any remaining value. If funds
              were collected, they will not automatically be refunded.
            </Alert>
          )}
          <SimpleSelect
            label="Select a resolution."
            labelVisuallyHidden
            options={[
              {
                key: 'approve-group',
                label: 'Approve',
                options: supportedResolutions.map((resolutionMethod) => ({
                  value: resolutionMethod,
                  label: crewClaimResolutionMethodEnumName[resolutionMethod],
                })),
              },
              {
                key: 'decline-group',
                label: 'Decline',
                options: [
                  {
                    value: 'Deny',
                    label: 'Deny',
                  },
                  {
                    value: 'Cancel',
                    label: 'Cancel',
                  },
                ],
              },
            ]}
            value={selectedResolution}
            onChange={setSelectedResolution}
          />
          {selectedResolution === 'Deny' && (
            <DenialModifier
              onCancel={cancelEdit}
              onSave={(denial) => {
                onChange({ kind: 'denied', denial });
                setShowEdit(false);
              }}
            />
          )}
          {selectedResolution === 'Cancel' && (
            <CancellationModifier
              onCancel={cancelEdit}
              onSave={(cancellation) => {
                onChange({ kind: 'canceled', cancellation });
                setShowEdit(false);
              }}
            />
          )}
          {selectedResolution !== 'Deny' &&
            selectedResolution !== 'Cancel' &&
            resolutionModifier[selectedResolution]({
              claimLineItem,
              onCancel: cancelEdit,
              onSave: (approval) => {
                onChange({
                  kind: 'approved',
                  approval,
                });
                setShowEdit(false);
              },
            })}
        </div>
      </Modal>
    </>
  );
}

function ClaimLineItemRevert({
  claimLineItem,
}: {
  claimLineItem: SupportedClaimLineItem;
}) {
  const [showConfirmRevert, setShowConfirmRevert] = useState(false);

  const { mutate: onChange, isPending } = useClaimLineItemUpdate(
    claimLineItem.id,
  );

  const revertToRequested = useCallback(() => {
    if (
      isRequestedAs(claimLineItem, CrewClaimResolutionMethodEnum.warrantyReview)
    ) {
      throw new Error('Cannot Revert Warranty Review');
    }

    if (
      isRequestedAs(claimLineItem, CrewClaimResolutionMethodEnum.refund) ||
      isRequestedAs(claimLineItem, CrewClaimResolutionMethodEnum.giftCard)
    ) {
      onChange({
        kind: 'approved',
        approval: {
          resolutionMethod: claimLineItem.requestedResolutionMethodEnum,
          amount: claimLineItem.requestedAmount ?? 0,
          wasResolvedExternally: !!claimLineItem.wasResolvedExternally,
        },
      });
      return;
    }

    // ? why isn't this already narrowed to variantExchange
    if (
      isRequestedAs(
        claimLineItem,
        CrewClaimResolutionMethodEnum.variantExchange,
      ) &&
      claimLineItem.variantExchangeLineItem
    ) {
      throw new Error('Cannot Revert Variant Exchange');
    }

    throw new Error(
      `Unhandled Revert of ${claimLineItem.requestedResolutionMethodEnum} for ClaimLineItem ${claimLineItem.id}`,
    );
  }, [claimLineItem, onChange]);

  return (
    <>
      <Action
        icon="undo"
        accessibilityLabel="Revert to Requested"
        variant="ghost"
        onClick={() => setShowConfirmRevert(true)}
        disabled={isPending}
        hidden={
          !canChangeResolution(claimLineItem) ||
          isDefaultApproval(claimLineItem) ||
          // cannot revert warranty review
          isRequestedAs(
            claimLineItem,
            CrewClaimResolutionMethodEnum.warrantyReview,
          ) ||
          // cannot revert variant exchange
          isRequestedAs(
            claimLineItem,
            CrewClaimResolutionMethodEnum.variantExchange,
          ) ||
          isRequestedAs(
            claimLineItem,
            CrewClaimResolutionMethodEnum.replacementOrder,
          )
        }
      />
      <ConfirmModal
        variant="primary"
        title="Revert to Requested"
        prompt="Are you sure you want to revert this claim line item to be approved as the requested resolution?"
        confirmText="Revert"
        show={showConfirmRevert}
        loading={isPending}
        onCancel={() => setShowConfirmRevert(false)}
        onConfirm={() => {
          revertToRequested();
          setShowConfirmRevert(false);
        }}
      />
    </>
  );
}

export default function ClaimLineItemActions({
  claimLineItem,
}: {
  claimLineItem: SupportedClaimLineItem;
}) {
  const isLocked = useIsLocked(claimLineItem);

  const { isPending } = useClaimLineItemUpdate(claimLineItem.id);

  if (isLocked) return null;

  if (isRequestedOther(claimLineItem)) return null;

  return (
    <div className="flex items-center gap-1">
      {isPending && <Loading />}
      <ClaimLineItemRevert claimLineItem={claimLineItem} />
      <ClaimLineItemEdit claimLineItem={claimLineItem} />
    </div>
  );
}
